import { useMemo, useState } from "react";

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------
const TEAL = "#0D5C63";
const WHITE = "#FFFFFF";
const BORDER = "#D8DADF";
const DARK = "#1A1A2E";
const GRAY = "#6B7280";
const GRAY_MID = "#9CA3AF";
const RED = "#C0392B";
const AMBER = "#B45309";
const GREEN = "#166534";
const CARD_BG = "#EDEEF0";

const fmt = (n) =>
  "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtK = (n) =>
  n >= 1000000 ? "$" + (n / 1000000).toFixed(1) + "M" : n >= 1000 ? "$" + (n / 1000).toFixed(0) + "K" : "$" + n;

// ---------------------------------------------------------------------------
// Static data
// ---------------------------------------------------------------------------
const PO_ORDERS = [
  { id: 2913, created: "Mar 19", brand: "Verde Cachaca", account: "Meridian Nevada", state: "NV", po: "4501481770", cases: 93, amount: 12381.14, status: "Staged" },
  { id: 2912, created: "Mar 19", brand: "Verde Cachaca", account: "Meridian Nevada", state: "NV", po: "4501481769", cases: 7, amount: 1131.69, status: "Staged" },
];

const PO_TOP_PRODUCTS = [
  { product: "Verde Cachaca Prata 750ml", cases: 96, amount: 12120.0 },
  { product: "Tierra Tequila Blanco 750ml", cases: 70, amount: 10940.0 },
 
];

// ---------------------------------------------------------------------------
// Reusable UI
// ---------------------------------------------------------------------------
function Alert({ type, children }) {
  const cfg = {
    warning: { bg: "#FEF3C7", border: "#FCD34D", left: AMBER, icon: "⚠" },
    critical: { bg: "#FDECEA", border: "#FCA5A5", left: RED, icon: "🚨" },
    info: { bg: "#EFF6FF", border: "#BFDBFE", left: "#2563EB", icon: "ℹ" }
  }[type];

  return (
    <div
      style={{
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        borderLeft: `4px solid ${cfg.left}`,
        borderRadius: 8,
        padding: "9px 14px",
        marginBottom: 12,
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
        fontSize: 12
      }}
    >
      <span style={{ fontSize: 13, flexShrink: 0 }}>{cfg.icon}</span>
      <div style={{ color: DARK, lineHeight: 1.5 }}>{children}</div>
    </div>
  );
}

export default function PurchaseOrdersPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortCol, setSortCol] = useState("created");
  const [sortDir, setSortDir] = useState("desc");

  const filtered = useMemo(() => {
    return PO_ORDERS
      .filter((r) => statusFilter === "All" || r.status === statusFilter)
      .filter(
        (r) =>
          !search ||
          r.account.toLowerCase().includes(search.toLowerCase()) ||
          r.brand.toLowerCase().includes(search.toLowerCase()) ||
          r.po.includes(search) ||
          String(r.id).includes(search)
      )
      .sort((a, b) => {
        if (sortCol === "created") return sortDir === "desc" ? b.id - a.id : a.id - b.id;
        const va = a[sortCol], vb = b[sortCol];
        if (typeof va === "string") return sortDir === "desc" ? vb.localeCompare(va) : va.localeCompare(vb);
        return sortDir === "desc" ? (vb || 0) - (va || 0) : (va || 0) - (vb || 0);
      });
  }, [search, sortCol, sortDir, statusFilter]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    else {
      setSortCol(col);
      setSortDir("desc");
    }
  };

  const statusCounts = {};
  PO_ORDERS.forEach((r) => {
    statusCounts[r.status] = (statusCounts[r.status] || 0) + 1;
  });

  const totalShipments = PO_ORDERS.length;
  const totalCases = PO_ORDERS.reduce((s, r) => s + r.cases, 0);
  const totalAmount = PO_ORDERS.reduce((s, r) => s + r.amount, 0);
  const stagedCount = statusCounts["Staged"] || 0;
  const stagedAmount = PO_ORDERS.filter((r) => r.status === "Staged").reduce((s, r) => s + r.amount, 0);

  const statusColor = (s) => {
    const cfg = {
      Staged: { bg: "rgba(180,83,9,0.08)", text: AMBER, border: "rgba(180,83,9,0.2)" },
      Shipped: { bg: "rgba(22,101,52,0.08)", text: GREEN, border: "rgba(22,101,52,0.2)" },
      "Invoice Sent": { bg: "rgba(190,24,93,0.08)", text: "#BE185D", border: "rgba(190,24,93,0.2)" },
      Completed: { bg: "rgba(13,92,99,0.08)", text: TEAL, border: "rgba(13,92,99,0.2)" }
    };
    return cfg[s] || { bg: CARD_BG, text: GRAY, border: BORDER };
  };

  const SortTH = ({ col, children, right }) => (
    <th
      onClick={() => handleSort(col)}
      style={{
        background: TEAL,
        color: WHITE,
        padding: "8px 10px",
        textAlign: right ? "right" : "left",
        fontSize: 10.5,
        fontWeight: 600,
        whiteSpace: "nowrap",
        cursor: "pointer",
        userSelect: "none"
      }}
    >
      {children}
      {sortCol === col ? (sortDir === "desc" ? " ↓" : " ↑") : ""}
    </th>
  );

  return (
    <div style={{ padding: "16px 18px", background: "#F4F5F7", minHeight: "100vh" }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: DARK, marginBottom: 12 }}>Purchase Orders</div>

      {stagedCount > 3 && (
        <Alert type="warning">
          <strong>{stagedCount} orders staged</strong> - {fmtK(stagedAmount)} in revenue waiting for pickup.
        </Alert>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 8, marginBottom: 16 }}>
        {[
          { label: "Total Orders", count: totalShipments, icon: "📋", color: TEAL, key: "All" },
          { label: "Staged", count: stagedCount, icon: "📦", color: AMBER, key: "Staged" },
          { label: "Shipped", count: statusCounts.Shipped || 0, icon: "🚚", color: GREEN, key: "Shipped" },
          { label: "Invoice Sent", count: statusCounts["Invoice Sent"] || 0, icon: "📄", color: "#BE185D", key: "Invoice Sent" },
          { label: "Completed", count: statusCounts.Completed || 0, icon: "✅", color: TEAL, key: "Completed" }
        ].map((s) => {
          const active = statusFilter === s.key;
          return (
            <div
              key={s.key}
              onClick={() => setStatusFilter(s.key)}
              style={{
                background: active ? s.color : WHITE,
                border: `2px solid ${active ? s.color : BORDER}`,
                borderRadius: 10,
                padding: "14px 16px",
                cursor: "pointer",
                textAlign: "center"
              }}
            >
              <div style={{ fontSize: 22 }}>{s.icon}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: active ? WHITE : s.color, marginTop: 4 }}>{s.count}</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: active ? "rgba(255,255,255,0.8)" : GRAY, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 2 }}>{s.label}</div>
            </div>
          );
        })}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 16 }}>
        {[
          { l: "Total Revenue (March)", v: fmt(totalAmount), sub: `${totalCases} cases across ${totalShipments} orders`, color: TEAL },
          { l: "Avg Order Value", v: fmt(totalAmount / totalShipments), sub: `${(totalCases / totalShipments).toFixed(0)} cases avg`, color: DARK },
          { l: "Staged Revenue", v: fmt(stagedAmount), sub: `${stagedCount} orders awaiting pickup`, color: stagedAmount > 20000 ? AMBER : GREEN },
          { l: "Top Account", v: "Meridian Nevada", sub: fmt(12381.14 + 1131.69) + " · 100 cases", color: DARK }
        ].map((k) => (
          <div key={k.l} style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 8, padding: "12px 14px" }}>
            <div style={{ fontSize: 10, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{k.l}</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: k.color }}>{k.v}</div>
            <div style={{ fontSize: 10, color: GRAY, marginTop: 2 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "14px 18px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: DARK, marginBottom: 10 }}>Top Products</div>
          {PO_TOP_PRODUCTS.map((p, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: i < PO_TOP_PRODUCTS.length - 1 ? `1px solid ${BORDER}` : "none" }}>
              <span style={{ fontSize: 11, color: DARK, flex: 1 }}>{p.product}</span>
              <span style={{ fontSize: 11, color: GRAY, marginRight: 10 }}>{p.cases} cs</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: TEAL }}>{fmt(p.amount)}</span>
            </div>
          ))}
        </div>

        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "14px 18px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: DARK, marginBottom: 10 }}>Top Accounts</div>
          {(() => {
            const accts = {};
            PO_ORDERS.forEach((r) => {
              if (!accts[r.account]) accts[r.account] = { cases: 0, amount: 0 };
              accts[r.account].cases += r.cases;
              accts[r.account].amount += r.amount;
            });
            return Object.entries(accts)
              .sort((a, b) => b[1].amount - a[1].amount)
              .slice(0, 5)
              .map(([name, d], i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: i < 4 ? `1px solid ${BORDER}` : "none" }}>
                  <span style={{ fontSize: 11, color: DARK, flex: 1, fontWeight: 500 }}>{name}</span>
                  <span style={{ fontSize: 11, color: GRAY, marginRight: 10 }}>{d.cases} cs</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: TEAL }}>{fmt(d.amount)}</span>
                </div>
              ));
          })()}
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
        <div style={{ position: "relative", flex: "0 0 300px" }}>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search orders..." style={{ width: "100%", padding: "7px 12px 7px 32px", border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: 12, color: DARK, background: WHITE, outline: "none" }} />
          <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: GRAY_MID }}>🔍</span>
        </div>
        <div style={{ marginLeft: "auto", fontSize: 11, color: GRAY }}>
          <strong style={{ color: DARK }}>{filtered.length}</strong> orders ·{" "}
          <strong style={{ color: DARK }}>{filtered.reduce((s, r) => s + r.cases, 0)}</strong> cases ·{" "}
          <strong style={{ color: TEAL }}>{fmt(filtered.reduce((s, r) => s + r.amount, 0))}</strong>
        </div>
      </div>

      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr>
              <SortTH col="id">Order #</SortTH>
              <SortTH col="created">Created</SortTH>
              <SortTH col="brand">Brand</SortTH>
              <SortTH col="account">Account</SortTH>
              <SortTH col="state">State</SortTH>
              <th style={{ background: TEAL, color: WHITE, padding: "8px 10px", fontSize: 10.5, fontWeight: 600 }}>PO</th>
              <SortTH col="cases" right>Cases</SortTH>
              <SortTH col="amount" right>Amount</SortTH>
              <SortTH col="status">Status</SortTH>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const sc = statusColor(r.status);
              return (
                <tr key={i} style={{ background: i % 2 === 0 ? WHITE : CARD_BG, borderBottom: `1px solid ${BORDER}` }}>
                  <td style={{ padding: "8px 10px", fontWeight: 700, color: TEAL }}>{r.id}</td>
                  <td style={{ padding: "8px 10px", color: GRAY, whiteSpace: "nowrap" }}>{r.created}</td>
                  <td style={{ padding: "8px 10px", color: DARK }}>{r.brand}</td>
                  <td style={{ padding: "8px 10px", fontWeight: 500, color: DARK }}>{r.account}</td>
                  <td style={{ padding: "8px 10px", color: GRAY }}>{r.state}</td>
                  <td style={{ padding: "8px 10px", color: GRAY, fontFamily: "monospace", fontSize: 10 }}>{r.po}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600 }}>{r.cases}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 700, color: DARK }}>{fmt(r.amount)}</td>
                  <td style={{ padding: "8px 10px" }}>
                    <span style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`, borderRadius: 4, padding: "3px 8px", fontSize: 10, fontWeight: 700 }}>{r.status}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

